import React, { useCallback, useRef, useState } from "react";
import { AllGtmEvents } from "analytics/gtm";
import cx from "classnames";

import { AvailableFilter } from "api/requests/consumerApi/searchShopCoreMenuItems";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";

import { InformationalModal } from "components/botanic/Modal";
import { MenuPage } from "components/MenuFilters/types/MenuFilters";
import TrackImpression from "components/TrackImpression";

import { FilterButton } from "./FilterButton";
import { defaultRenderChips, Facet } from "./FilteredGrid";

export type MobileFilterShortcutsType = React.ComponentType<{
  onShortcutClick: (filterSectionKey?: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  availableFilters?: any;
  menuPage?: MenuPage;
}>;

interface Props {
  confirmationButtonText: string;
  facets: Facet[];
  children?: React.ReactNode;
  shortcuts?: MobileFilterShortcutsType;
  onShortcutClick?: (filterKey?: string) => void;
  renderChips?: (facets: Facet[]) => React.ReactNode;
  filterPanelImpression?: AllGtmEvents;
  availableFilters?: AvailableFilter;
  onMobileFilterButtonClick?: () => void;
  mobileClassName?: string;
  menuPage?: MenuPage;
}

export const MobileFilters: React.FC<Props> = ({
  children,
  confirmationButtonText,
  facets,
  shortcuts: Shortcuts,
  onShortcutClick,
  availableFilters,
  filterPanelImpression,
  renderChips = defaultRenderChips,
  onMobileFilterButtonClick,
  mobileClassName,
  menuPage,
}): JSX.Element => {
  const { publishEvent } = useEventTracker();
  const [modalActive, setModalActive] = useState(false);
  const modalButtonRef = useRef(null);
  const { clearAllFilters } = useFilters();

  const numberOfFiltersSelected = facets.length;
  const filterTitle = `Filters${
    numberOfFiltersSelected ? ` (${numberOfFiltersSelected})` : ""
  }`;

  const openModal = useCallback((filterSectionKey?: string) => {
    if (onShortcutClick) {
      onShortcutClick(filterSectionKey);
    }
    setModalActive(true);
  }, []);

  return (
    <>
      <div
        className={cx(
          "flex items-center overflow-x-auto overflow-y-hidden whitespace-nowrap pb-sm mt-md mb-xs gap-1",
          mobileClassName
        )}
      >
        {filterPanelImpression && (
          <TrackImpression
            trackingFunction={() => publishEvent(filterPanelImpression)}
          />
        )}
        <FilterButton
          filterCount={numberOfFiltersSelected}
          onClick={() => {
            openModal();
            if (onMobileFilterButtonClick) {
              onMobileFilterButtonClick();
            }
          }}
          ref={modalButtonRef}
        />
        {facets.length === 0 && Shortcuts && (
          <Shortcuts
            onShortcutClick={openModal}
            availableFilters={availableFilters}
            menuPage={menuPage}
          />
        )}
        {renderChips(facets)}
      </div>

      {modalActive && (
        <InformationalModal
          title={filterTitle}
          returnFocusRef={modalButtonRef}
          onDismissed={() => setModalActive(false)}
          expository
        >
          <div
            className="-mx-lg -mt-lg h-full flex flex-col justify-between"
            style={{ height: "calc(100% - 65px)" }}
          >
            <div>{children}</div>
            <div
              className="flex sticky w-full p-lg bg-white border-t border-light-grey"
              style={{ bottom: -16 }}
            >
              <button
                onClick={() => clearAllFilters(["sort"])}
                className="button button--text flex-shrink-0 px-xxl text-sm text-default"
              >
                clear all
              </button>
              <button
                className="button button--tertiary text-sm"
                onClick={() => setModalActive(false)}
              >
                {confirmationButtonText}
              </button>
            </div>
          </div>
        </InformationalModal>
      )}
    </>
  );
};
